import { LocationSelector } from "@/components/LocationSelector";
import { NotificationButton } from "@/components/NotificationButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRegion } from "@/contexts/RegionContext";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import {
  Building2,
  CarFront,
  ChevronDown,
  Home,
  House,
  Languages,
  MapPin,
  Menu,
  Newspaper,
  Search,
  ShoppingBag,
  User,
  Users,
} from "lucide-react";
import { Link } from "wouter";

const radiusOptions = [
  { value: "3", label: "3km" },
  { value: "10", label: "10km" },
  { value: "20", label: "20km" },
  { value: "all", labelKo: "전체", labelEn: "All" },
] as const;

export function TopBar() {
  const { selectedRadius, setSelectedRadius } = useRegion();
  const { isKorean, setLocale } = useTopicLocale();

  const radiusLabel = (() => {
    const option = radiusOptions.find((item) => item.value === selectedRadius);
    if (!option) return "10km";
    if ("label" in option) return option.label;
    return isKorean ? option.labelKo : option.labelEn;
  })();

  const primaryLinks = [
    {
      href: "/news",
      label: isKorean ? "뉴스" : "News",
      icon: Newspaper,
    },
    {
      href: "/market",
      label: isKorean ? "동네 마켓" : "Local Market",
      icon: MapPin,
      anchor: false,
    },
    {
      href: "/community-talk",
      label: isKorean ? "동네 톡톡" : "Neighbourhood Talk",
      icon: Users,
    },
    {
      href: "/topics",
      label: isKorean ? "생활문제 해결" : "Life Solutions",
      icon: Search,
    },
    {
      href: "/business-directory",
      label: isKorean ? "업체찾기" : "Find Services",
      icon: Building2,
    },
  ];

  const marketLinks = [
    {
      href: "/market",
      label: isKorean ? "전체 매물" : "All listings",
      icon: ShoppingBag,
    },
    {
      href: "/market?category=community",
      label: isKorean ? "중고물품" : "Used goods",
      icon: ShoppingBag,
    },
    {
      href: "/market?category=used-car",
      label: isKorean ? "자동차" : "Cars",
      icon: CarFront,
    },
    {
      href: "/market?category=real-estate",
      label: isKorean ? "부동산" : "Property",
      icon: House,
    },
  ];

  return (
    <div className="sticky top-0 z-50 flex h-12 items-center justify-between border-b border-line-subtle bg-surface-0 px-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="-m-2 rounded-lg p-2 text-ink-2 transition-colors hover:bg-surface-1"
            aria-label={isKorean ? "전체 메뉴 열기" : "Open full menu"}
            data-testid="button-mobile-full-menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={10}
          className="max-h-[calc(100vh-5rem)] w-72 overflow-y-auto"
        >
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>OZK</span>
            <span className="text-xs font-normal text-muted-foreground">
              {isKorean ? "호주생활 가이드" : "Life in Australia"}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <Link href="/" className="flex w-full items-center">
              <Home className="mr-3 h-4 w-4" />
              {isKorean ? "홈" : "Home"}
            </Link>
          </DropdownMenuItem>

          {primaryLinks.map((item) => {
            const Icon = item.icon;
            const content = (
              <>
                <Icon className="mr-3 h-4 w-4" />
                {item.label}
              </>
            );

            return (
              <DropdownMenuItem key={item.href} asChild>
                {item.anchor ? (
                  <a href={item.href} className="flex w-full items-center">
                    {content}
                  </a>
                ) : (
                  <Link href={item.href} className="flex w-full items-center">
                    {content}
                  </Link>
                )}
              </DropdownMenuItem>
            );
          })}

          <DropdownMenuSeparator />
          <DropdownMenuLabel>
            {isKorean ? "통합 동네마켓" : "Unified Local Market"}
          </DropdownMenuLabel>
          {marketLinks.map((item) => {
            const Icon = item.icon;
            return (
              <DropdownMenuItem key={item.href} asChild>
                <Link href={item.href} className="flex w-full items-center">
                  <Icon className="mr-3 h-4 w-4" />
                  {item.label}
                </Link>
              </DropdownMenuItem>
            );
          })}

          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/profile" className="flex w-full items-center">
              <User className="mr-3 h-4 w-4" />
              {isKorean ? "내 정보" : "Profile"}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setLocale(isKorean ? "en" : "ko")}
          >
            <Languages className="mr-3 h-4 w-4" />
            {isKorean ? "English로 보기" : "한국어로 보기"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex min-w-0 items-center gap-1">
        <LocationSelector
          variant="compact"
          className="h-8 max-w-[8rem] px-2 text-xs [&>span]:truncate"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-8 items-center gap-1 rounded-lg bg-surface-1 px-2 text-xs text-ink-2 transition-colors hover:bg-surface-2"
              aria-label={isKorean ? "검색 반경 변경" : "Change search radius"}
            >
              <span>{radiusLabel}</span>
              <ChevronDown className="h-3 w-3 text-ink-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-32">
            {radiusOptions.map((option) => {
              const label =
                "label" in option
                  ? option.label
                  : isKorean
                    ? option.labelKo
                    : option.labelEn;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() =>
                    setSelectedRadius(
                      option.value as "3" | "10" | "20" | "all",
                    )
                  }
                  className={
                    selectedRadius === option.value ? "bg-surface-2" : ""
                  }
                >
                  {label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-1">
        <NotificationButton
          buttonClassName="rounded-lg p-2 text-ink-2 transition-colors hover:bg-surface-1"
          iconClassName="h-5 w-5"
          badgeClassName="-right-1 -top-1"
        />
        <Link href="/search">
          <button
            type="button"
            className="-m-1 rounded-lg p-2 text-ink-2 transition-colors hover:bg-surface-1"
            aria-label={isKorean ? "검색" : "Search"}
          >
            <Search className="h-5 w-5" />
          </button>
        </Link>
      </div>
    </div>
  );
}
